import BaseTibbit from '@appblocks/codegeneration/Tibbits/_BaseTibbit';
import Tibbits from '@appblocks/codegeneration/Tibbits/index';
import { CodeBlock, BlockType, SettingType, TibboDataType } from '@appblocks/codegeneration/types';

export default class Tibbit_22 extends BaseTibbit {
    constructor() {
        super();
        this.id = 'tibbit_22';
        this.name = 'Tibbit 22';
        this.fileLinks = [
            // 'ssi\\ssi.tbs',
            // 'ssi\\ssi.tbh',
        ];
        this.files = [
            'libraries/super_i2c/super_i2c.tbh',
            'libraries/super_i2c/super_i2c.tbs',
            'libraries/tibbits/tbt22/tbt22.tbh',
            'libraries/tibbits/tbt22/tbt22.tbs',
            'libraries/ssi/ssi.tbh',
            'libraries/ssi/ssi.tbs',
        ];
        const settings = [
            {
                id: 'MAX_NUM',
                name: 'Max Number of Tibbits',
                settingType: SettingType.HIDDEN,
                required: true,
                default: '1',
            },
            {
                id: 'NAME',
                name: 'Name',
                settingType: SettingType.NAME,
                required: true,
                default: '',
            },
            {
                id: 'TEMP_NAME',
                name: 'Name',
                settingType: SettingType.NAME,
                dataType: TibboDataType.FLOAT,
                required: true,
                default: 'temp_rtd',
            },
            {
                id: 'USESSI',
                name: 'Mode',
                settingType: SettingType.OPTION,
                required: true,
                readonly: true,
                default: 'NO',
                options: [
                    {
                        name: 'I2C mode',
                        value: 'YES',
                    },
                    {
                        name: 'Direct I/O (bit-banging)',
                        value: 'NO',
                    },
                ],
            },
            {
                id: 'CONV',
                name: 'Conversion Mode',
                settingType: SettingType.OPTION,
                required: true,
                default: 'tbt22_conversion_with_50_hz_mode',
                options: [
                    {
                        name: '50 Hz',
                        value: 'tbt22_conversion_with_50_hz_mode',
                    },
                    {
                        name: '60 Hz',
                        value: 'tbt22_conversion_with_60_hz_mode',
                    },
                ],
            },
            {
                id: 'SENSOR',
                name: 'Sensor Type',
                settingType: SettingType.OPTION,
                required: true,
                default: 'PT100',
                options: [
                    {
                        name: 'PT100',
                        value: 'PT100',
                    },
                    {
                        name: 'PT200',
                        value: 'PT200',
                    },
                    {
                        name: 'PT500',
                        value: 'PT500',
                    },
                    {
                        name: 'PT1000',
                        value: 'PT1000',
                    },
                ],
            },
        ];

        this.settings = this.settings.concat(settings);
    }

    getCodeBlocks() {
        const blocks: Array<CodeBlock> = [];

        if (this.project.runtime === 'simulator') {
            return [];
        }

        const socket = Tibbits.getSocket(this.getSettingValue('SOCKET'), this.project.device.id);
        const TEMP_NAME = this.getSettingValue('TEMP_NAME');
        const CONV = this.getSettingValue('CONV');
        const SENSOR = this.getSettingValue('SENSOR');

        blocks.push({
            location: 'boot',
            blockType: BlockType.CODE,
            template: `
    'Tibbit 22 setup on socket ${this.getSettingValue('SOCKET')}
    '================================================================
    tbt_rtd_init("${this.getSettingValue('NAME')}", ${this.getSettingValue('NAME')}_DATA, ${this.getSettingValue('NAME')}_CLK, ${this.getSettingValue('NAME')}_INT, ${this.getSettingValue('NAME')}_CHANNEL_NUM, ${this.getSettingValue('USESSI')})
    tbt22_rtd_config(${CONV},${SENSOR},${this.getSettingValue('NAME')}_INT, ${this.getSettingValue('NAME')}_CHANNEL_NUM)
    'To read temperature:
    'dim temp_data, fault_detect, fault_status as byte
    'tbt22_rtd_get_temperature(temp_data, fault_detect, fault_status, ${this.getSettingValue('NAME')}_INT, ${this.getSettingValue('NAME')}_CHANNEL_NUM)
    '-----------------------------------------------------------------

        `,
        });

        blocks.push({
            location: 'GLOBAL_DECLARATIONS',
            blockType: BlockType.CODE,
            template: `
declare ${this.getSettingValue('NAME')}_CHANNEL_NUM as byte
        `,
        });

        blocks.push({
            location: 'GLOBAL_DIMS',
            blockType: BlockType.CODE,
            template: `
dim ${this.getSettingValue('NAME')}_CHANNEL_NUM as byte
        `,
        });

        blocks.push({
            location: 'GLOBAL_DEFINES',
            blockType: BlockType.CODE,
            template: `
#define TBT22_MAX_NUM_OF_SOCKS ${this.getSettingValue('MAX_NUM')}`,
        });


        blocks.push({
            location: 'GLOBAL_CONSTS',
            blockType: BlockType.CODE,
            template: `
const ${this.getSettingValue('NAME')}_CLK=${socket?.pins[0]}
const ${this.getSettingValue('NAME')}_DATA=${socket?.pins[1]}
const ${this.getSettingValue('NAME')}_INT=${socket?.pins[3]}

            `,
        });

        const interruptNumber = Tibbits.getSocket(this.getSettingValue('SOCKET'), this.project.device.id)?.interrupt;

        blocks.push({
            location: `var_${TEMP_NAME}_update`,
            blockType: BlockType.CODE,
            template: `
    dim value as real
    dim detect,status as byte
    dim flag as no_yes=NO

    tbt22_rtd_get_temperature(value,detect,status,${interruptNumber},${this.getSettingValue('NAME')}_CHANNEL_NUM)

    if detect and RTD_FAULT_DETECTION_FINISHED then
        flag=YES
        sys.debugprint("RTD Fault detection: finished")
    end if
    if detect and RTD_FAULT_DETECTION_AUTOMATIC_FAULT then
        flag=YES
        sys.debugprint("RTD Fault detection: automatic fault")
    end if
    if detect and RTD_FAULT_DETECTION_MANUAL_CYCLE_1 then
        flag=YES
        sys.debugprint("RTD Fault detection: manual 1")
    end if
    if detect and RTD_FAULT_DETECTION_MANUAL_CYCLE_2 then
        flag=YES
        sys.debugprint("RTD Fault detection: manual 2")
    end if
    if status and RTD_FAULT_HIGH_THRESHOLD then
        flag=YES
        sys.debugprint("RTD High Threshold")
        sys.debugprint("DATA:"+hex(value))
    end if
    if status and RTD_FAULT_LOW_THRESHOLD then
        flag=YES
        sys.debugprint("RTD Low Threshold")
        sys.debugprint("DATA:"+hex(value))
    end if
    if status and RTD_FAULT_REFIN then
        flag=YES
        sys.debugprint("REFIN- > 0.85*Vbias")
    end if
    if status and RTD_FAULT_REFIN_FORCE then
        flag=YES
        sys.debugprint("REFIN- < 0.85*Vbias")
    end if
    if status and RTD_FAULT_RTDIN_FORCE then
        flag=YES
        sys.debugprint("RTDIN- < 0.85*Vbias")
    end if
    if status and RTD_FAULT_VOLTAGE then
        flag=YES
        sys.debugprint("Over/Undervoltage")
    end if

    if flag=NO then
        'sys.debugprint(ftostr(value,FTOSTR_MODE_AUTO,6)+" C")
        'sys.debugprint(ftostr(value,FTOSTR_MODE_AUTO,6))
        var_${TEMP_NAME}_update_completed(value)
    else
        'Clear the fault status and keep continue to aquire the data
        tbt22_rtd_config(${CONV},${SENSOR},${interruptNumber},${this.getSettingValue('NAME')}_CHANNEL_NUM)
    end if`,
        });

        return blocks;
    }
}
